import { LoadingSpinner } from "components/LoadingSpinner";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Sidebar } from "primereact/sidebar";
import styled from "styled-components";
import {
  IBenefitSelectionExplanation,
  IExplainStep,
} from "types/BenefitSelection/BenefitSelection";

import { Surface } from "./Surface";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
    font-size: 0.85rem;
  }
`;

// Extracted so `react/no-unstable-nested-components` does not see a component redefined per render.
//
// ⚠️ `kind` IS DELIBERATELY IGNORED. Whether a step lives in code or in this account's saved
// configuration is a fact about who can change it, not about what it does — and a reader asking how
// a copay is chosen was being handed that distinction on every other row.
const looksAtCellBody = (step: IExplainStep) => <span>{step.looksAt}</span>;

const ifFoundCellBody = (step: IExplainStep) => (
  <span>
    {step.ifFound}
    {step.stop ? " Stop." : ""}
  </span>
);

const ifNotCellBody = (step: IExplainStep) => <span>{step.ifNot}</span>;

const indexCellBody = (step: IExplainStep) => (
  <span className="font-medium">{step.index}</span>
);

function StepTable({
  steps,
  testId,
}: {
  steps: IExplainStep[];
  testId: string;
}) {
  return (
    <StyledDataTable value={steps} dataKey="index" data-testid={testId}>
      <Column header="" style={{ width: "3rem" }} body={indexCellBody} />
      <Column header="Looks at" body={looksAtCellBody} />
      <Column header="If it finds a value" body={ifFoundCellBody} />
      <Column header="If not" body={ifNotCellBody} />
    </StyledDataTable>
  );
}

interface IExplainDrawerProps {
  visible: boolean;
  onHide: () => void;
  explanation?: IBenefitSelectionExplanation;
  /** One benefit's steps, or null for every benefit. */
  benefit: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * CLE-2792 — "How each benefit is selected", rendered from `GET .../benefitSelection/explain`.
 *
 * 🚨 NOTHING HERE IS COMPOSED ON THE CLIENT. Every sentence in these tables comes from the endpoint,
 * which builds them from the account's saved configuration and from the engine's own constants,
 * pinned by a drift test. A screen that assembled its own prose next to the generated version would
 * be a second account of the behaviour, and the hand-written one goes wrong first — which is exactly
 * how a support call ends up with a confident wrong answer.
 *
 * So this component's whole job is layout: sections, tables, and the note lines.
 *
 * 🚨 ONE BENEFIT AT A TIME WHEN ONE WAS ASKED ABOUT. "How it is selected" is asked from a row — the
 * copay, say — and answering with five benefits' worth of steps means the reader has to find the one
 * they asked about inside the answer. "Before any benefit is read" is shown either way, because
 * every benefit below it is conditional on those steps. Opened from a row that names no benefit,
 * the panel shows them all.
 */
export function ExplainDrawer({
  visible,
  onHide,
  explanation,
  benefit,
  isLoading,
  isError,
}: IExplainDrawerProps) {
  const shown = benefit
    ? (explanation?.benefits ?? []).filter((entry) => entry.benefit === benefit)
    : explanation?.benefits ?? [];

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: "min(58rem, 100vw)" }}
      data-testid="benefitSelection.explainDrawer"
    >
      <Surface className="flex flex-column gap-4">
        <div className="flex flex-column gap-1">
          <h3 className="m-0">How each benefit is selected</h3>
          <span className="text-sm text-color-secondary">
            {explanation?.accountName ?? ""}
          </span>
        </div>

        {isLoading && <LoadingSpinner />}

        {isError && (
          <StyledMessage
            severity="error"
            data-testid="benefitSelection.explainDrawer.error"
            text="The explanation could not be loaded."
          />
        )}

        {explanation && (
          <>
            {explanation.beforeAnyBenefit.length > 0 && (
              <div className="flex flex-column gap-2">
                <span className="font-medium text-sm">
                  Before any benefit is read{" "}
                  <span className="text-color-secondary font-normal">
                    applies to every field below
                  </span>
                </span>
                <StepTable
                  steps={explanation.beforeAnyBenefit}
                  testId="benefitSelection.explainDrawer.beforeAnyBenefit"
                />
              </div>
            )}

            {shown.map((entry) => (
              <div
                key={entry.benefit}
                className="flex flex-column gap-2"
                data-testid={`benefitSelection.explainDrawer.${entry.benefit}`}
              >
                <span className="font-medium text-sm">
                  {entry.label}{" "}
                  <span className="text-color-secondary font-normal">
                    {entry.headline}
                  </span>
                </span>
                <StepTable
                  steps={entry.steps}
                  testId={`benefitSelection.explainDrawer.${entry.benefit}.steps`}
                />
                {entry.notes.map((note) => (
                  <span key={note} className="text-xs text-color-secondary">
                    {note}
                  </span>
                ))}
              </div>
            ))}
          </>
        )}
      </Surface>
    </Sidebar>
  );
}
